<script lang="ts" setup>
const { $preview } = useNuxtApp()
</script>

<template>
  <div class="epc-landing-wide">
    <Header :hide="false" :landing="true" wide />
    <slot />
    <LazyFooter wide />
    <LazyNewPreview v-if="$preview" />
  </div>
</template>

<style>
.epc-landing-wide,
.testimonial-dialog {
  --epc-font-display: 'Neue Haas Grotesk Display', sans-serif;
  --epc-font-text: 'Neue Haas Grotesk Text', sans-serif;
  --epc-type-h1: clamp(52px, 5vw, 84px);
  --epc-type-h2: clamp(40px, 4vw, 64px);
  --epc-type-h3: clamp(28px, 2.25vw, 34px);
  --epc-type-h4: clamp(22px, 1.65vw, 26px);
  --epc-type-h5: clamp(17px, 1.15vw, 18px);
  --epc-copy-large: clamp(18px, 1.2vw, 20px);
  --epc-copy-default: 16px;
  --epc-copy-small: 14px;
  --epc-copy-xs: 12px;
  --epc-tracking-display-xl: -.025em;
  --epc-tracking-display-lg: -.018em;
  --epc-tracking-display-md: -.01em;
  --epc-tracking-display-sm: -.004em;
  --epc-interaction-duration: 180ms;
  --epc-interaction-ease: cubic-bezier(.23, 1, .32, 1);
  --epc-header-morph-duration: 600ms;
  --epc-header-morph-ease: cubic-bezier(.16, 1, .3, 1);
}

.epc-landing-wide > .header--wide {
  top: 0;
  border-bottom: 0;
  background: transparent;
  pointer-events: none;
  transition: top var(--epc-header-morph-duration) var(--epc-header-morph-ease);
}

.epc-landing-wide > .header--wide > .header-primary {
  width: auto;
  margin-inline: 0;
  overflow: hidden;
  border: 1px solid rgba(0, 43, 64, .08);
  border-radius: 0;
  background: rgba(255, 255, 255, .96);
  box-shadow: none;
  -webkit-backdrop-filter: blur(12px) saturate(120%);
  backdrop-filter: blur(12px) saturate(120%);
  pointer-events: auto;
  transition:
    margin-inline-start var(--epc-header-morph-duration) var(--epc-header-morph-ease),
    margin-inline-end var(--epc-header-morph-duration) var(--epc-header-morph-ease),
    border-color 420ms var(--epc-header-morph-ease),
    border-radius var(--epc-header-morph-duration) var(--epc-header-morph-ease),
    background-color 420ms var(--epc-header-morph-ease),
    box-shadow 420ms var(--epc-header-morph-ease),
    -webkit-backdrop-filter 420ms var(--epc-header-morph-ease),
    backdrop-filter 420ms var(--epc-header-morph-ease);
}

.epc-landing-wide > .header--wide > .header-primary > .header-primary__inner {
  width: 100%;
  max-width: none;
  padding-inline: 48px;
}

.epc-landing-wide > .header--wide.header--scrolled {
  top: clamp(12px, 1.25vw, 24px);
}

.epc-landing-wide > .header--wide.header--scrolled > .header-primary {
  width: auto;
  margin-inline: max(48px, calc((100vw - 1824px) / 2));
  border-color: rgba(255, 255, 255, .72);
  border-radius: 16px;
  background: rgba(255, 255, 255, .78);
  box-shadow: 0 12px 36px rgba(0, 43, 64, .12);
  -webkit-backdrop-filter: blur(18px) saturate(140%);
  backdrop-filter: blur(18px) saturate(140%);
}

.epc-landing-wide > .header--wide.header--scrolled > .header-primary > .header-primary__inner {
  padding-inline: 48px;
}

@media (max-width: 680px) {
  .epc-landing-wide > .header--wide {
    top: 0;
  }

  .epc-landing-wide > .header--wide > .header-primary > .header-primary__inner {
    padding-inline: 16px;
  }

  .epc-landing-wide > .header--wide.header--scrolled {
    top: 12px;
  }

  .epc-landing-wide > .header--wide.header--scrolled > .header-primary {
    width: auto;
    margin-inline: 12px;
    border-radius: 12px;
  }

  .epc-landing-wide > .header--wide.header--scrolled > .header-primary > .header-primary__inner {
    padding-inline: 16px;
  }
}

@media (prefers-reduced-transparency: reduce), (prefers-contrast: more) {
  .epc-landing-wide > .header--wide > .header-primary {
    border-color: rgba(0, 43, 64, .18);
    background: rgba(255, 255, 255, .98);
    -webkit-backdrop-filter: none;
    backdrop-filter: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .epc-landing-wide > .header--wide,
  .epc-landing-wide > .header--wide > .header-primary,
  .epc-landing-wide > .header--wide > .header-primary > .header-primary__inner {
    transition: none;
  }
}

.epc-landing-wide #top .solar-v3 h1,
.epc-landing-wide #top .solar-v3 h2,
.epc-landing-wide #top .solar-v3 h3,
.epc-landing-wide #top .solar-v3 h4,
.epc-landing-wide #top .solar-v3 h5,
body .testimonial-dialog.testimonial-dialog h2 {
  font-family: var(--epc-font-display);
  font-weight: 500;
  text-wrap: balance;
}

.epc-landing-wide #top .solar-v3 .nextwell-plan h3 {
  font-weight: 600;
}

.epc-landing-wide #top .solar-v3 h1 {
  font-size: var(--epc-type-h1);
  font-weight: 600;
  line-height: .96;
  letter-spacing: var(--epc-tracking-display-xl);
}

.epc-landing-wide #top .solar-v3 h2 {
  font-size: var(--epc-type-h2);
  line-height: 1;
  letter-spacing: var(--epc-tracking-display-lg);
}

body .testimonial-dialog.testimonial-dialog #testimonial-dialog-title {
  font-size: 32px;
  line-height: 1.06;
  letter-spacing: -.03em;
}

.epc-landing-wide #top .solar-v3 h3 {
  font-size: var(--epc-type-h3);
  line-height: 1.06;
  letter-spacing: var(--epc-tracking-display-md);
}

.epc-landing-wide #top .solar-v3 h4 {
  font-size: var(--epc-type-h4);
  line-height: 1.1;
  letter-spacing: var(--epc-tracking-display-sm);
}

.epc-landing-wide #top .solar-v3 h5 {
  font-size: var(--epc-type-h5);
  line-height: 1.2;
  letter-spacing: 0;
}

.epc-landing-wide #top .solar-v3 section p,
.epc-landing-wide #top .solar-v3 section li,
.epc-landing-wide #top .solar-v3 section dt,
.epc-landing-wide #top .solar-v3 section dd,
.epc-landing-wide header :is(p, a, button, span),
.epc-landing-wide footer :is(p, a, button, span, label, input),
.testimonial-dialog :is(p, small) {
  font-family: var(--epc-font-text);
}

.epc-landing-wide #top .solar-v3 section p {
  font-size: var(--epc-copy-default);
  line-height: 1.6;
}

.epc-landing-wide #top .solar-v3 .type-copy-large,
.epc-landing-wide #top .solar-v3 .v3-copy {
  font-size: var(--epc-copy-large);
  line-height: 1.6;
}

.epc-landing-wide #top .solar-v3 .type-copy-small,
.epc-landing-wide #top .solar-v3 .process-card p,
.epc-landing-wide #top .solar-v3 .nextwell-plan__bottom > p,
.epc-landing-wide #top .solar-v3 .testimonial-library__header > p {
  font-size: var(--epc-copy-small);
  line-height: 1.55;
}

.epc-landing-wide #top .solar-v3 .feature-fact-card p {
  font-size: var(--epc-copy-default);
  line-height: 1.5;
}

.epc-landing-wide #top .solar-v3 .type-copy-xs,
.epc-landing-wide #top .solar-v3 .v3-eyebrow,
.epc-landing-wide #top .solar-v3 .field-help,
body .testimonial-dialog.testimonial-dialog .type-copy-xs {
  font-size: var(--epc-copy-xs);
  line-height: 1.45;
}

.epc-landing-wide footer :is(p, a, button, span, label, input) {
  font-size: var(--epc-copy-default);
  line-height: 1.55;
}

.epc-landing-wide footer nav span,
.epc-landing-wide footer form label,
.epc-landing-wide footer .type-copy-small {
  font-size: var(--epc-copy-small);
}

.epc-landing-wide footer .type-copy-xs {
  font-size: var(--epc-copy-xs);
  line-height: 1.5;
}

.epc-landing-wide #top .solar-v3 .cta-button {
  display: inline-flex;
  height: 50px;
  box-sizing: border-box;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border: 1px solid transparent;
  border-radius: 999px;
  padding: 0 22px;
  font-family: var(--epc-font-text);
  font-size: 14px;
  font-weight: 700;
  line-height: 1;
  text-align: center;
  text-decoration: none;
  white-space: nowrap;
  cursor: pointer;
  transition:
    transform var(--epc-interaction-duration) var(--epc-interaction-ease),
    background-color var(--epc-interaction-duration) ease,
    border-color var(--epc-interaction-duration) ease,
    color var(--epc-interaction-duration) ease;
}

.epc-landing-wide #top .solar-v3 .cta-button--fill {
  border-color: #6dbf44;
  background: #6dbf44;
  color: #fff;
}

.epc-landing-wide #top .solar-v3 .cta-button--dark {
  border-color: #002b40;
  background: #002b40;
  color: #fff;
}

.epc-landing-wide #top .solar-v3 .cta-button--outline {
  border-color: rgba(0, 43, 64, .4);
  background: transparent;
  color: #002b40;
}

.epc-landing-wide #top .solar-v3 .cta-button--outline-light {
  border-color: rgba(255, 255, 255, .48);
  background: transparent;
  color: #fff;
}

.epc-landing-wide #top .solar-v3 .cta-button--full {
  width: 100%;
}

.epc-landing-wide #top .solar-v3 .cta-button--sticky {
  width: 100%;
  border-radius: 0;
}

.epc-landing-wide #top .solar-v3 .cta-button:focus-visible {
  outline: 3px solid rgba(109, 191, 68, .48);
  outline-offset: 4px;
}

.epc-landing-wide #top .solar-v3 .cta-button:active {
  transform: translateY(0) scale(.98);
  box-shadow: none;
}

.epc-landing-wide #top .solar-v3 .cta-button:disabled,
.epc-landing-wide #top .solar-v3 .cta-button[aria-disabled='true'] {
  cursor: not-allowed;
  opacity: .5;
}

@media (hover: hover) and (pointer: fine) {
  .epc-landing-wide #top .solar-v3 .cta-button:not(:disabled, [aria-disabled='true']):hover {
    transform: translateY(-2px);
  }

  .epc-landing-wide #top .solar-v3 .cta-button--fill:not(:disabled, [aria-disabled='true']):hover {
    border-color: var(--cta-fill-hover-border, #002b40);
    background: var(--cta-fill-hover-bg, #fff);
    color: var(--cta-fill-hover-color, #002b40);
  }

  .epc-landing-wide #top .solar-v3 .cta-button--dark:not(:disabled, [aria-disabled='true']):hover {
    border-color: #fff;
    background: #fff;
    color: #002b40;
  }

  .epc-landing-wide #top .solar-v3 .cta-button--outline:not(:disabled, [aria-disabled='true']):hover {
    border-color: #002b40;
    background: #002b40;
    color: #fff;
  }

  .epc-landing-wide #top .solar-v3 .cta-button--outline-light:not(:disabled, [aria-disabled='true']):hover {
    border-color: #fff;
    background: #fff;
    color: #002b40;
  }

  .epc-landing-wide #top .solar-v3 .package-card-cta.cta-button--dark:not(:disabled, [aria-disabled='true']):hover {
    border-color: #6dbf44;
    background: #6dbf44;
    color: #fff;
  }
}

@media (min-width: 768px) {
  .epc-landing-wide #top .solar-v3 .cta-button--sticky {
    display: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .epc-landing-wide #top .solar-v3 .cta-button {
    transition:
      background-color var(--epc-interaction-duration) ease,
      border-color var(--epc-interaction-duration) ease,
      color var(--epc-interaction-duration) ease;
  }

  .epc-landing-wide #top .solar-v3 .cta-button:active {
    transform: none;
  }
}
</style>
